// libraries
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { notification, Select, Typography } from 'antd';
import { blue } from '@ant-design/colors';
import styled from 'styled-components';

// services
import * as GroupService from '../../../../../services/bb-api/group';
import { formatDate } from '../../../../../utils/date';
import { txt } from '../../../../../locales/i18n';

const statuses: Record<string, any>[] = [
	{ id: -4, name: txt('statuses.deleted admin') },
	{ id: -3, name: txt('statuses.deleted user') },
	{ id: -2, name: txt('statuses.hidden admin') },
	{ id: -1, name: txt('statuses.hidden user') },
	{ id: 1, name: txt('statuses.activated user') },
	{ id: 2, name: txt('statuses.activated admin') },
	{ id: 3, name: txt('statuses.edit user') },
	{ id: 4, name: txt('statuses.edit admin') },
];

export function GroupPageMessages(props: any): JSX.Element {
	const [messages, setMessages] = useState<Record<string, any>[]>([]);
	const { id }: Record<string, any> = useParams();

	useEffect(() => {
		async function getMessages(): Promise<void> {
			const messages: Record<string, any>[] = await GroupService.getMessages(id);
			setMessages(messages);
		}

		getMessages();
	}, [id]);

	return (
		<StyledDiv>
			<div className="message-feed">
				{messages.map((message: Record<string, any>) => (
					<Message key={message.id} content={message} />
				))}
			</div>
		</StyledDiv>
	);
}

function Message(props: any): JSX.Element {
	const content: Record<string, any> = props.content;
	const [loading, setLoading] = useState<boolean>(false);
	const [status, setStatus] = useState<number>(content.status);

	const messageRef = useRef<HTMLDivElement>(null);
	const location = useLocation();

	useEffect(() => {
		const message = messageRef.current;
		if (message === null) return;

		const query: URLSearchParams = new URLSearchParams(location.search);
		if (query.get('message') === String(content.id)) message.scrollIntoView();
	}, [location.search, content.id]);

	async function handleChangeStatus(status: number): Promise<void> {
		setLoading(true);

		try {
			await GroupService.updateMessage(content.groupId, content.id, status);
			setStatus(status);
			notification.success({ message: txt('notification.update successful') });
		} catch (error) {
			const message: string = (error as Error).name;
			const description: string = (error as Error).message;
			notification.error({ message, description });
		}

		setLoading(false);
	}

	return (
		<div ref={messageRef} className="message-container">
			<div className="message-header">
				<div>
					<Link to={`/users/${content.userId}`}>
						{content.username} <Typography.Text type="secondary">{content.userMunicipality}</Typography.Text>
					</Link>
					<br />
					<Typography.Text type="secondary">{formatDate(content.created)}</Typography.Text>
				</div>
				<Select value={status} dropdownMatchSelectWidth={false} bordered={false} loading={loading} onChange={handleChangeStatus}>
					{statuses.map((status: Record<string, any>) => (
						<Select.Option key={status.id} value={status.id}>
							{status.name}
						</Select.Option>
					))}
				</Select>
			</div>

			<div className={`message-content ${status < 0 && 'hidden'}`}>{content.body}</div>

			<div className="comment-feed">
				{content.comments.map((comment: Record<string, any>) => (
					<Comment key={comment.id} comment={comment} />
				))}
			</div>
		</div>
	);
}

function Comment(props: any): JSX.Element {
	const comment: Record<string, any> = props.comment;
	const [loading, setLoading] = useState<boolean>(false);
	const [status, setStatus] = useState<number>(comment.status);

	const messageRef = useRef<HTMLDivElement>(null);
	const location = useLocation();

	useEffect(() => {
		const message = messageRef.current;
		if (message === null) return;

		const query: URLSearchParams = new URLSearchParams(location.search);
		if (query.get('message') === String(comment.id)) message.scrollIntoView();
	}, [location.search, comment.id]);

	async function handleChangeStatus(status: number): Promise<void> {
		setLoading(true);

		try {
			await GroupService.updateMessage(comment.groupId, comment.id, status);
			setStatus(status);
			notification.success({ message: txt('notification.update successful') });
		} catch (error) {
			const message: string = (error as Error).name;
			const description: string = (error as Error).message;
			notification.error({ message, description });
		}

		setLoading(false);
	}

	return (
		<div ref={messageRef} className="comment-container">
			<div className="comment-header">
				<div>
					<Link to={`/users/${comment.userId}`}>
						{comment.username} <Typography.Text type="secondary">{comment.userMunicipality}</Typography.Text>
					</Link>
					<br />
					<Typography.Text type="secondary">{formatDate(comment.created)}</Typography.Text>
				</div>
				<Select value={status} dropdownMatchSelectWidth={false} bordered={false} loading={loading} onChange={handleChangeStatus}>
					{statuses.map((status: Record<string, any>) => (
						<Select.Option key={status.id} value={status.id}>
							{status.name}
						</Select.Option>
					))}
				</Select>
			</div>

			<div className={`comment-content ${status < 0 && 'hidden'}`}>{comment.body}</div>
		</div>
	);
}

const StyledDiv = styled.div`
	display: flex;
	justify-content: center;

	.message-feed {
		display: flex;
		flex-direction: column;
		gap: 1.8em;
		width: fit-content;
		max-width: 32rem;
	}

	.comment-feed {
		display: flex;
		flex-direction: column;
		align-items: end;
		gap: 1.8em;
	}

	.message-container {
		display: flex;
		flex-direction: column;
		gap: 0.8em;

		.message-header {
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			width: fit-content;
			margin-inline: 1ch;
		}

		.message-content {
			background-color: #eee;
			padding: 1em;
			border-radius: 1em;
			width: fit-content;
			border-radius: 1em 1em 1em 0;

			&.hidden {
				opacity: 0.5;
			}
		}
	}

	.comment-container {
		display: flex;
		flex-direction: column;
		gap: 0.8em;
		max-width: 80%;
		align-items: flex-end;

		.comment-header {
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			width: fit-content;
			margin-inline: 1ch;
		}

		.comment-content {
			background-color: ${blue[4]};
			color: white;
			padding: 1em;
			border-radius: 1em;
			align-self: flex-end;
			border-radius: 1em 1em 0 1em;

			&.hidden {
				opacity: 0.5;
			}
		}
	}
`;
